import { useState, useEffect, useCallback } from 'react';

const normalizeBooleanFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return ['1', 'true', 'yes', 'on', 'required'].includes(normalized);
};

const isWidgetRequired = (widget = {}) => {
  const status = String(widget?.options?.status || '').trim().toLowerCase();
  if (status === 'required') return true;
  if (status === 'optional') return false;
  return (
    normalizeBooleanFlag(widget?.required) ||
    normalizeBooleanFlag(widget?.options?.required) ||
    normalizeBooleanFlag(widget?.options?.validation?.required)
  );
};

const parseCheckboxValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (Array.isArray(value)) return value.some((entry) => parseCheckboxValue(entry));
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  if (['0', 'false', 'no', 'off', 'unchecked', 'null', 'undefined'].includes(normalized)) {
    return false;
  }
  return true;
};

const isWidgetValueCompleted = (widgetType, value) => {
  const normalizedType = String(widgetType || '').trim().toLowerCase();
  if (normalizedType === 'checkbox') {
    // Required checkboxes should only count as completed when checked.
    return parseCheckboxValue(value);
  }
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * Custom hook for managing widget state with localStorage persistence
 * 
 * @param {string} documentId - Unique document identifier
 * @param {Array} placeholders - Array of placeholder objects from document
 * @returns {Object} Widget state management functions and data
 */
function useWidgetState(documentId, placeholders = []) {
  const STORAGE_KEY = `danasign_widget_state_${documentId}`;

  // Initialize widget state from localStorage or empty object
  const [widgetState, setWidgetState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load widget state from localStorage:', error);
      return {};
    }
  });

  // Save widget state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(widgetState));
    } catch (error) {
      console.error('Failed to save widget state to localStorage:', error);
    }
  }, [widgetState, STORAGE_KEY]);

  /**
   * Update a widget's value and completion status
   * @param {string} widgetId - Unique widget identifier (pageNumber-widgetIndex)
   * @param {any} value - Widget value
   * @param {Object} metadata - Additional widget metadata (type, required, pageNumber, widgetIndex)
   */
  const updateWidget = useCallback((widgetId, value, metadata = {}) => {
    setWidgetState((prevState) => ({
      ...prevState,
      [widgetId]: {
        value,
        completed: isWidgetValueCompleted(metadata.type || prevState[widgetId]?.type, value),
        type: metadata.type || prevState[widgetId]?.type || 'unknown',
        required:
          metadata.required !== undefined
            ? normalizeBooleanFlag(metadata.required)
            : prevState[widgetId]?.required || false,
        pageNumber: metadata.pageNumber || prevState[widgetId]?.pageNumber || 1,
        widgetIndex: metadata.widgetIndex !== undefined ? metadata.widgetIndex : prevState[widgetId]?.widgetIndex || 0,
        interactive:
          metadata.interactive !== undefined
            ? metadata.interactive
            : prevState[widgetId]?.interactive !== undefined
              ? prevState[widgetId]?.interactive
              : true,
        isPrefill:
          metadata.isPrefill !== undefined
            ? metadata.isPrefill
            : prevState[widgetId]?.isPrefill === true,
        autoSuggested: false
      }
    }));
  }, []);

  /**
   * Get a widget's state
   * @param {string} widgetId - Unique widget identifier
   * @returns {Object|null} Widget state or null if not found
   */
  const getWidget = useCallback((widgetId) => {
    return widgetState[widgetId] || null;
  }, [widgetState]);

  /**
   * Check if all required widgets are filled
   * @returns {boolean} True if all required widgets are completed
   */
  const isAllRequiredFilled = useCallback(() => {
    const requiredWidgets = Object.values(widgetState).filter(
      w => w.required && w.interactive !== false
    );
    if (requiredWidgets.length === 0) return true;
    return requiredWidgets.every(w => w.completed);
  }, [widgetState]);

  /**
   * Get the next unfilled required widget
   * @returns {Object|null} Widget info {widgetId, pageNumber, widgetIndex} or null if all filled
   */
  const getNextUnfilledWidget = useCallback(() => {
    // Get all unfilled required widgets sorted by page and index
    const unfilledWidgets = Object.entries(widgetState)
      .filter(([_, widget]) => widget.required && !widget.completed)
      .map(([widgetId, widget]) => ({
        widgetId,
        pageNumber: widget.pageNumber,
        widgetIndex: widget.widgetIndex
      }))
      .sort((a, b) => {
        if (a.pageNumber !== b.pageNumber) {
          return a.pageNumber - b.pageNumber;
        }
        return a.widgetIndex - b.widgetIndex;
      });

    return unfilledWidgets.length > 0 ? unfilledWidgets[0] : null;
  }, [widgetState]);

  /**
   * Clear all widget state (used after successful submission)
   */
  const clearState = useCallback(() => {
    setWidgetState({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear widget state from localStorage:', error);
    }
  }, [STORAGE_KEY]);

  /**
   * Calculate completion progress percentage
   * @returns {number} Progress percentage (0-100)
   */
  const getProgress = useCallback(() => {
    const requiredWidgets = Object.values(widgetState).filter(
      w => w.required && w.interactive !== false
    );
    if (requiredWidgets.length === 0) return 100;
    
    const completedRequired = requiredWidgets.filter(w => w.completed).length;
    return Math.round((completedRequired / requiredWidgets.length) * 100);
  }, [widgetState]);

  /**
   * Initialize widget state from placeholders
   * This should be called once when placeholders are loaded
   * @param {Array} pages - Array of page objects with pos array
   * @param {Object} recipientInfo - Recipient information for prefilling (name, email)
   */
  const initializeFromPlaceholders = useCallback((pages, recipientInfo = {}, options = {}) => {
    const disableInitialsPrefill = options?.disableInitialsPrefill === true;
    // Helper to calculate initials from name
    const calculateInitials = (name) => {
      if (!name) return '';
      return name
        .split(' ')
        .filter(part => part.length > 0)
        .map(part => part[0].toUpperCase())
        .join('');
    };
    
    setWidgetState((prevState) => {
      const nextState = {};

      // pages is an array of { pageNumber, pos: [...] }
      pages.forEach((page) => {
        const positions = page.pos || [];
        positions.forEach((widget, widgetIndex) => {
          const widgetId = `${page.pageNumber}-${widgetIndex}`;
          const widgetType = String(widget?.type || 'signature').trim().toLowerCase();
          const roleName = String(widget?.Role || page?.Role || '').trim().toLowerCase();
          const isPrefill =
            widget?.isPrefill === true ||
            widget?.options?.isPrefill === true ||
            roleName === 'prefill';
          const hasResponse =
            widget?.options &&
            Object.prototype.hasOwnProperty.call(widget.options, 'response');
          const existingState = prevState[widgetId];

          let nextValue =
            existingState && Object.prototype.hasOwnProperty.call(existingState, 'value')
              ? existingState.value
              : null;
          let autoPrefillSuggestion = false;

          if (!existingState || !Object.prototype.hasOwnProperty.call(existingState, 'value')) {
            if (hasResponse) {
              if (disableInitialsPrefill && widgetType === 'initials') {
                nextValue = null;
              } else {
                nextValue = widget.options.response;
              }
            } else if (widgetType === 'initials' && recipientInfo.name && !disableInitialsPrefill) {
              nextValue = calculateInitials(recipientInfo.name);
              autoPrefillSuggestion = true;
            } else if (widgetType === 'name' && recipientInfo.name) {
              nextValue = recipientInfo.name;
              autoPrefillSuggestion = true;
            } else if (widgetType === 'email' && recipientInfo.email) {
              nextValue = recipientInfo.email;
              autoPrefillSuggestion = true;
            }
          }

          const nextRequired = isPrefill ? false : isWidgetRequired(widget);
          const nextCompleted =
            (existingState?.autoSuggested === true && !hasResponse)
              ? false
              : autoPrefillSuggestion && !existingState
              ? false
              : isWidgetValueCompleted(widgetType, nextValue);

          nextState[widgetId] = {
            value: nextValue,
            completed: nextCompleted,
            type: widgetType,
            required: nextRequired,
            pageNumber: page.pageNumber,
            widgetIndex,
            interactive: !isPrefill,
            isPrefill,
            autoSuggested:
              existingState?.autoSuggested === true && !hasResponse
                ? true
                : autoPrefillSuggestion && !hasResponse
          };
        });
      });

      return nextState;
    });
  }, []);

  return {
    widgetState,
    updateWidget,
    getWidget,
    isAllRequiredFilled,
    getNextUnfilledWidget,
    clearState,
    getProgress,
    initializeFromPlaceholders
  };
}

export default useWidgetState;
